import { createEntityAdapter, createSelector, EntityState } from "@reduxjs/toolkit";
import { Apartment, Convert, GetStrOwnersResponse, ManagementFeeRatio, NeteraCredentialLabel, PayoutRatio } from "@/models/responses";
import { bookingManagementApi } from "@/store/booking-management-api";
import { formatDateToISODate } from "@/utils/dates";
import { useSelector } from "react-redux";
import { SelectOption } from "@/lib/molecules/select";
import { RootState } from "@/store/store";
import { useAuth } from "@/hooks/useAuth";

const inceptionDate = new Date();
inceptionDate.setFullYear(2023, 0, 1);
const futureDate = new Date();
futureDate.setFullYear(2080, 0, 1);

const apartmentsAdapter = createEntityAdapter<Apartment>();
const adapterSelectors = apartmentsAdapter.getSelectors();

const nextMonthDate = new Date();
nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
const previousMonthDate = new Date();
previousMonthDate.setMonth(previousMonthDate.getMonth() - 1);

const initialApartmentsState: EntityState<Apartment, number> = apartmentsAdapter.getInitialState();

export interface ApartmentUpdate extends Apartment {
    effectiveFrom?: Date;
}

/**
 * A per-apartment, effective-dated cleaning rate (fee + cost). Mirrors
 * `PayoutRatio`/`ManagementFeeRatio`, but cleaning rates differ in two ways:
 *  - `effectiveFrom` may be ANY calendar date (not snapped to a month start);
 *  - they carry two monetary fields (fee + cost) which the backend serialises
 *    as `decimal.Decimal` (JSON number, or string for high precision), hence
 *    `number | string`.
 */
export interface CleaningRate {
    id?: number;
    apartmentId: number;
    effectiveFrom: string;
    cleaningFee: number | string;
    cleaningCost: number | string;
}

/**
 * The Add/Update response echoes the persisted rate and reports whether the
 * triggered interval recompute touched a past-dated (already-closed)
 * reservation — the EC-004 historical-change warning.
 */
export interface CleaningRateMutationResult {
    cleaningRate: CleaningRate;
    affectedHistoricalPeriods: boolean;
}

/** The DELETE response reports only the EC-004 historical-change flag. */
export interface RemoveCleaningRateResult {
    affectedHistoricalPeriods: boolean;
}

/**
 * A per-apartment, effective-dated monthly business fee
 * (`manual-business-fee/FR-001`).
 *
 * Hand-written rather than generated: this resource deliberately ships no JSON
 * schema, so there is no quicktype `Convert` for it — the same call the cleaning
 * rates made.
 *
 * `amount` is `number | string` because the backend serialises it as a
 * `decimal.Decimal`, which marshals as a QUOTED string ("25.00"), while the UI
 * posts it back as a plain number. `effectiveFrom` is an ISO date-only string
 * and is always the first of a month (FR-003).
 */
export interface BusinessFee {
    id: number;
    apartmentId: number;
    amount: number | string;
    effectiveFrom: string;
}

/**
 * The Add/Update response echoes the persisted entry. Both mutations share the
 * shape: a POST for an effective date the apartment already has updates that
 * entry rather than creating a second one (EC-006), so the two cannot be told
 * apart by their response.
 */
export interface BusinessFeeMutationResult {
    businessFee: BusinessFee;
}

/**
 * One entry of the business-fee readiness report
 * (`manual-business-fee/FR-020`): an apartment with at least one operational
 * month no fee entry covers, and the FIRST such month.
 *
 * The month is part of the answer because the report is deliberately stronger
 * than "has no fee at all" — an apartment whose earliest entry post-dates its
 * first operational month is reported too (AS-015), and it HAS a fee. Only the
 * month tells the operator, and the add dialog, which gap to fill.
 *
 * `firstUncoveredMonth` arrives as a marshalled Go `time.Time`
 * ("2026-01-01T00:00:00Z"), the same as `BusinessFee.effectiveFrom`, and always
 * lands on the first of a month.
 */
export interface UncoveredApartment {
    apartmentId: number;
    firstUncoveredMonth: string;
}

export const apartmentsSlice = bookingManagementApi.injectEndpoints({
    endpoints: (builder) => ({
        getApartments: builder.query<EntityState<Apartment, number>, boolean>({
            query: (isAdmin) => ({
                url: `${isAdmin ? "admin/" : ""}apartments?fromDate=${formatDateToISODate(inceptionDate)}&endDate=${formatDateToISODate(futureDate)}`,
            }),
            transformResponse: (response: string) => {
                const apartments = Convert.toGetApartmentsResponse(JSON.stringify(response)).apartments;
                return apartmentsAdapter.setAll(initialApartmentsState, apartments);
            },
            providesTags: ["Apartments"],
        }),
        addApartment: builder.mutation<Apartment, Apartment>({
            query: (apartment: Apartment) => ({
                url: `admin/apartments`,
                method: "POST",
                body: apartment,
            }),
            transformResponse: (response: string) => {
                return Convert.toGetApartmentResponse(JSON.stringify(response)).apartment;
            },

            invalidatesTags: ["Apartments"],
        }),
        putApartment: builder.mutation<EntityState<ApartmentUpdate, number>, ApartmentUpdate>({
            query: (apartment: ApartmentUpdate) => ({
                url: `admin/apartments/${apartment.id}`,
                method: "PUT",
                body: apartment,
            }),
            invalidatesTags: ["Apartments"],
        }),
        decommissionApartment: builder.mutation<void, { id: number; removalDate: string }>({
            query: ({ id, removalDate }) => ({
                url: `admin/apartments/${id}/decommission`,
                method: "POST",
                body: { removalDate },
            }),
            invalidatesTags: ["Apartments"],
        }),
        reactivateApartment: builder.mutation<void, number>({
            query: (id: number) => ({
                url: `admin/apartments/${id}/reactivate`,
                method: "POST",
            }),
            invalidatesTags: ["Apartments"],
        }),
        addPayoutRatio: builder.mutation<void, PayoutRatio>({
            query: (ratio: PayoutRatio) => ({
                url: `admin/apartments/payout-ratios`,
                method: "POST",
                body: ratio,
            }),
            invalidatesTags: ["Apartments"],
        }),
        addManagementFeeRatio: builder.mutation<void, ManagementFeeRatio>({
            query: (ratio: ManagementFeeRatio) => ({
                url: `admin/apartments/management-fee-ratios`,
                method: "POST",
                body: ratio,
            }),
            invalidatesTags: ["Apartments"],
        }),
        addCleaningRate: builder.mutation<CleaningRateMutationResult, CleaningRate>({
            query: (rate: CleaningRate) => ({
                url: `admin/apartments/cleaning-rates`,
                method: "POST",
                body: {
                    apartmentId: rate.apartmentId,
                    effectiveFrom: rate.effectiveFrom,
                    cleaningFee: rate.cleaningFee,
                    cleaningCost: rate.cleaningCost,
                },
            }),
            invalidatesTags: ["Apartments"],
        }),
        updateCleaningRate: builder.mutation<CleaningRateMutationResult, CleaningRate>({
            query: (rate: CleaningRate) => ({
                url: `admin/apartments/${rate.apartmentId}/cleaning-rates/${rate.id}`,
                method: "PUT",
                body: {
                    apartmentId: rate.apartmentId,
                    effectiveFrom: rate.effectiveFrom,
                    cleaningFee: rate.cleaningFee,
                    cleaningCost: rate.cleaningCost,
                },
            }),
            invalidatesTags: ["Apartments"],
        }),
        removeCleaningRate: builder.mutation<RemoveCleaningRateResult, { apartmentId: number; id: number }>({
            query: ({ apartmentId, id }) => ({
                url: `admin/apartments/${apartmentId}/cleaning-rates/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Apartments"],
        }),
        getNeteraCredentialLabels: builder.query<NeteraCredentialLabel[], void>({
            // D-P3: registered at the admin group root (`admin/netera-credentials`),
            // NOT under `apartments/`, to avoid gin's static-vs-`:aid` param clash.
            // Returns id + label only — never secrets (FR-010).
            query: () => ({
                url: `admin/netera-credentials`,
            }),
            transformResponse: (response: string) => {
                return Convert.toGetNeteraCredentialLabelsResponse(JSON.stringify(response)).labels ?? [];
            },
        }),
        getStrOwners: builder.query<GetStrOwnersResponse, void>({
            // D-P4: dedicated admin handler (`admin/str-owners`), NOT under
            // `apartments/`, to dodge gin's static-vs-`:aid` param clash — mirrors
            // getNeteraCredentialLabels. Returns the owner list + the configured
            // default owner id so the add form can pre-select it (AS-004). Never
            // returns Taxis passwords (FR-004).
            query: () => ({
                url: `admin/str-owners`,
            }),
            transformResponse: (response: string) => {
                return Convert.toGetStrOwnersResponse(JSON.stringify(response));
            },
        }),
        getCleaningRateReadiness: builder.query<number[], void>({
            query: () => ({
                url: `admin/apartments/cleaning-rates/readiness`,
            }),
            transformResponse: (response: { apartmentIds: number[] }) => {
                return response.apartmentIds ?? [];
            },
            providesTags: ["Apartments"],
        }),
        // The business fee is money, not a ratio: it is charged per apartment per
        // month at the amount whose `effectiveFrom` is the latest on or before that
        // month (manual-business-fee/FR-007..FR-011).
        getCurrentBusinessFees: builder.query<BusinessFee[], void>({
            // Every apartment that HAS a fee, one entry each. An apartment with none
            // is absent rather than listed at zero — it belongs to the readiness
            // report below (FR-014, EC-010).
            query: () => ({
                url: `admin/apartments/business-fees`,
            }),
            transformResponse: (response: { businessFees: BusinessFee[] }) => {
                return response.businessFees ?? [];
            },
            providesTags: ["Apartments"],
        }),
        getApartmentBusinessFees: builder.query<BusinessFee[], number>({
            // One apartment's full history, most recent effective date first (FR-015).
            query: (apartmentId: number) => ({
                url: `admin/apartments/${apartmentId}/business-fees`,
            }),
            transformResponse: (response: { businessFees: BusinessFee[] }) => {
                return response.businessFees ?? [];
            },
            providesTags: ["Apartments"],
        }),
        getBusinessFeeReadiness: builder.query<UncoveredApartment[], void>({
            // The apartments with at least one uncovered operational month, each with
            // the first month it is uncovered for — what the page's banner is drawn
            // from (FR-020, FR-021).
            query: () => ({
                url: `admin/apartments/business-fees/readiness`,
            }),
            transformResponse: (response: { uncovered: UncoveredApartment[] }) => {
                return response.uncovered ?? [];
            },
            providesTags: ["Apartments"],
        }),
        // All three mutations invalidate "Apartments", which the readiness query
        // provides: filling the last uncovered month must make the banner disappear,
        // and deleting the only entry must bring it back, without the operator
        // reloading the page.
        addBusinessFee: builder.mutation<BusinessFeeMutationResult, Omit<BusinessFee, "id">>({
            query: (fee: Omit<BusinessFee, "id">) => ({
                url: `admin/apartments/business-fees`,
                method: "POST",
                body: {
                    apartmentId: fee.apartmentId,
                    amount: fee.amount,
                    effectiveFrom: fee.effectiveFrom,
                },
            }),
            invalidatesTags: ["Apartments"],
        }),
        updateBusinessFee: builder.mutation<BusinessFeeMutationResult, BusinessFee>({
            // Both the apartment and the entry are named by the path; the body carries
            // no apartment id, so it cannot move an entry between apartments.
            query: (fee: BusinessFee) => ({
                url: `admin/apartments/${fee.apartmentId}/business-fees/${fee.id}`,
                method: "PUT",
                body: {
                    amount: fee.amount,
                    effectiveFrom: fee.effectiveFrom,
                },
            }),
            invalidatesTags: ["Apartments"],
        }),
        removeBusinessFee: builder.mutation<void, { apartmentId: number; id: number }>({
            // Returns the apartment to whatever its remaining entries resolve to — the
            // earlier one, or zero (FR-018, EC-012). The server answers with no body.
            query: ({ apartmentId, id }) => ({
                url: `admin/apartments/${apartmentId}/business-fees/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Apartments"],
        }),
    }),
});

const selectApartmentsState = (state: RootState, isAdmin: boolean) => {
    const result = apartmentsSlice.endpoints.getApartments.select(isAdmin)(state);
    return result.data ?? initialApartmentsState;
};

const selectAllApartments: (state: RootState, isAdmin: boolean) => Apartment[] = createSelector([selectApartmentsState], (state) =>
    apartmentsAdapter.getSelectors().selectAll(state),
);

const selectAllApartmentsUnsorted: (state: RootState, isAdmin: boolean) => Apartment[] = createSelector([selectApartmentsState], (state) =>
    adapterSelectors.selectAll(state),
);

const selectActiveApartments: (state: RootState, isAdmin: boolean) => Apartment[] = createSelector([selectAllApartmentsUnsorted], (apartments) => {
    return [...apartments]
        .filter(
            (apartment) =>
                apartment.dateAdded <= nextMonthDate &&
                // `dateRemoved` is undefined for apartments that have never been removed
                // (the API omits the field) and null when explicitly cleared. Both mean
                // "active". Only when set to a real Date in the past does the apartment
                // count as removed (give a one-month grace window via previousMonthDate).
                (apartment.dateRemoved == null || apartment.dateRemoved >= previousMonthDate),
        )
        .sort((a, b) => a.name.localeCompare(b.name));
});

const selectAllApartmentEntities: (state: RootState, isAdmin: boolean) => Record<string, Apartment> = createSelector([selectApartmentsState], (state) => {
    return adapterSelectors.selectEntities(state);
});

const selectApartmentOptions: (state: RootState, isAdmin: boolean) => SelectOption[] = createSelector(
    [selectActiveApartments],
    (apartments): SelectOption[] => {
        return [
            {
                value: "0",
                label: "All",
            },
            ...apartments.map((apartment) => ({
                value: apartment.id.toString(),
                label: apartment.name,
            })),
        ];
    },
);

const selectAllApartmentOptions: (state: RootState, isAdmin: boolean) => SelectOption[] = createSelector(
    [selectAllApartments],
    (apartments): SelectOption[] => {
        return [
            {
                value: "0",
                label: "All",
            },
            ...apartments.map((apartment) => ({
                value: apartment.id.toString(),
                label: apartment.name,
            })),
        ];
    },
);

export const useAllApartments = () => {
    const { user } = useAuth();
    return useSelector((state: RootState) => (user ? selectAllApartments(state, user.isAdmin) : []));
};

export const useActiveApartments = () => {
    const { user } = useAuth();
    return useSelector((state: RootState) => (user ? selectActiveApartments(state, user.isAdmin) : []));
};

export const useActiveApartmentEntities = () => {
    const { user } = useAuth();
    return useSelector((state: RootState) => (user ? selectAllApartmentEntities(state, user.isAdmin) : {}));
};

export const useAllApartmentEntities = () => {
    const { user } = useAuth();
    return useSelector((state: RootState) => (user ? selectAllApartmentEntities(state, user.isAdmin) : {}));
};

export const useApartment = (apartmentId: number) => {
    const { user } = useAuth();
    return useSelector((state: RootState) => {
        return selectAllApartmentEntities(state, user?.isAdmin || false)[apartmentId];
    });
};

export const useActiveApartmentOptions = () => {
    const { user } = useAuth();
    return useSelector((state: RootState) => (user ? selectApartmentOptions(state, user.isAdmin) : []));
};

export const useAllApartmentOptions = () => {
    const { user } = useAuth();
    return useSelector((state: RootState) => (user ? selectAllApartmentOptions(state, user.isAdmin) : []));
};

export const {
    useGetApartmentsQuery,
    useAddApartmentMutation,
    usePutApartmentMutation,
    useAddManagementFeeRatioMutation,
    useAddPayoutRatioMutation,
    useAddCleaningRateMutation,
    useUpdateCleaningRateMutation,
    useRemoveCleaningRateMutation,
    useGetCleaningRateReadinessQuery,
    useGetCurrentBusinessFeesQuery,
    useGetApartmentBusinessFeesQuery,
    useGetBusinessFeeReadinessQuery,
    useAddBusinessFeeMutation,
    useUpdateBusinessFeeMutation,
    useRemoveBusinessFeeMutation,
    useGetNeteraCredentialLabelsQuery,
    useGetStrOwnersQuery,
    useDecommissionApartmentMutation,
    useReactivateApartmentMutation,
} = apartmentsSlice;
